import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { submitPasswordReset } from "@/lib/api";
import { formatApiError } from "@/lib/auth";

const INPUT_CLS =
  "bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-11 px-3 w-full";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is incomplete. Please request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await submitPasswordReset(token, password);
      setDone(true);
      toast.success("Password updated — you're signed in.");
      setTimeout(() => navigate("/"), 1200);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message || "Could not reset the password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen flex items-center justify-center p-4 bg-[#F0F9FF]"
    >
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-2xl shadow-slate-900/10 overflow-hidden">
        <div className="border-b-4 border-[#2563EB] px-6 py-5">
          <div className="heading text-lg font-bold tracking-tight">
            CARGOLOAD<span className="text-[#2563EB]">.PRO</span>
          </div>
        </div>
        <div className="p-6">
          {done ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h2 className="heading text-2xl font-bold mb-1">Password updated</h2>
              <p className="text-sm text-slate-500">Taking you back to the app…</p>
            </div>
          ) : (
            <>
              <h2 className="heading text-2xl font-bold mb-1 flex items-center gap-2">
                <KeyRound className="w-6 h-6 text-[#2563EB]" /> Pick a new password
              </h2>
              <p className="text-sm text-slate-500 mb-5">
                Make it at least 6 characters. We'll sign you in as soon as you save it.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label htmlFor="rp-pass" className="overline block mb-1.5">New password</label>
                  <Input
                    id="rp-pass"
                    data-testid="reset-password-input"
                    type="password"
                    className={INPUT_CLS}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    required
                    autoComplete="new-password"
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="rp-confirm" className="overline block mb-1.5">Confirm password</label>
                  <Input
                    id="rp-confirm"
                    data-testid="reset-password-confirm"
                    type="password"
                    className={INPUT_CLS}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    minLength={6}
                    required
                    autoComplete="new-password"
                  />
                </div>
                {error && (
                  <div
                    data-testid="reset-password-error"
                    className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  data-testid="reset-password-submit"
                  disabled={busy || !password || !confirm || !token}
                  className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-bold mono uppercase tracking-widest text-xs h-11 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? (
                    <span className="flex items-center gap-2 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                    </span>
                  ) : "Save new password →"}
                </Button>
              </form>
              <div className="text-center mt-4 text-xs text-slate-500 mono">
                <Link to="/" className="text-[#2563EB] hover:underline">← Back to CargoLoad</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
